import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Camera, CheckCircle2, Loader2, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Public page (no auth) — client opens the link sent by AARIFI office and
 * uploads photos for ONLY the photo types the office pre-selected for this
 * specific order.  Mobile-friendly, uses native camera capture.
 */
export default function ClientPhotoUploadPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [uploadingType, setUploadingType] = useState(null);
  const [uploadedByType, setUploadedByType] = useState({}); // { typeName: count }
  const inputRefs = useRef({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/client-upload/${token}`);
        if (!cancelled) setInfo(res.data);
      } catch (e) {
        if (!cancelled) setErrorMsg(e?.response?.data?.detail || "Invalid upload link.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handlePick = (type) => {
    const el = inputRefs.current[type];
    if (el) el.click();
  };

  const handleChange = async (type, ev) => {
    const files = Array.from(ev.target.files || []);
    if (!files.length) return;
    setUploadingType(type);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append("files", f));
      formData.append("photo_types", files.map(() => type).join(","));
      const res = await axios.post(`${API}/client-upload/${token}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const added = res.data?.uploaded || 0;
      setUploadedByType(prev => ({ ...prev, [type]: (prev[type] || 0) + added }));
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploadingType(null);
      // Reset the input so the same file can be re-uploaded if needed
      if (inputRefs.current[type]) inputRefs.current[type].value = "";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-sm w-full text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-gray-800 mb-1">Link not valid</h1>
          <p className="text-sm text-gray-500">{errorMsg || "Please contact AARIFI office for a new link."}</p>
        </div>
      </div>
    );
  }

  const types = info.selected_photo_types || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-md mx-auto p-4 pb-12">
        <header className="bg-white border border-gray-200 rounded-lg p-5 mb-4" data-testid="client-upload-header">
          <div className="flex items-center gap-3 mb-3">
            <img src="/aarifi-icon-small.png" alt="AARIFI" className="h-9 w-9 object-contain" />
            <div className="min-w-0">
              <p className="text-[12px] font-bold text-gray-800 leading-tight">AARIFI Inspection</p>
              <p className="text-[10px] text-gray-500 leading-tight">Safeguard Properties Affiliate</p>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3 space-y-1.5 text-[12.5px]">
            <div className="flex gap-2">
              <span className="text-gray-400 w-[68px] shrink-0">Order #</span>
              <span className="font-semibold text-gray-800" data-testid="client-upload-order-number">{info.order_number || "—"}</span>
            </div>
            {(info.address_full || info.address) && (
              <div className="flex gap-2">
                <span className="text-gray-400 w-[68px] shrink-0">Address</span>
                <span className="font-medium text-gray-800" data-testid="client-upload-address">{info.address_full || info.address}</span>
              </div>
            )}
            {info.ph_name && (
              <div className="flex gap-2">
                <span className="text-gray-400 w-[68px] shrink-0">Name</span>
                <span className="font-medium text-gray-800" data-testid="client-upload-name">{info.ph_name}</span>
              </div>
            )}
            {info.ph_phone && (
              <div className="flex gap-2">
                <span className="text-gray-400 w-[68px] shrink-0">Phone</span>
                <span className="font-medium text-gray-800" data-testid="client-upload-phone">{info.ph_phone}</span>
              </div>
            )}
          </div>

          <p className="text-[12px] text-gray-600 mt-4 leading-relaxed border-t border-gray-100 pt-3">
            Please upload the requested photos below. You may upload multiple photos for each item.
          </p>
        </header>

        {types.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-5 text-center">
            <p className="text-sm text-gray-500">No photos requested for this order yet. Please check back later.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {types.map(type => {
              const count = uploadedByType[type] || 0;
              const busy = uploadingType === type;
              const done = count > 0;
              return (
                <div
                  key={type}
                  className={`bg-white border rounded-lg p-4 transition ${done ? "border-emerald-200 bg-emerald-50/40" : "border-gray-200"}`}
                  data-testid={`client-photo-row-${type.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-gray-800 truncate">{type}</p>
                      {done && (
                        <p className="text-[11px] text-emerald-700 mt-0.5">
                          {count} photo{count > 1 ? "s" : ""} received
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handlePick(type)}
                      disabled={busy}
                      className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold rounded-md transition ${
                        busy
                          ? "bg-gray-100 text-gray-400"
                          : done
                            ? "bg-emerald-600 text-white hover:bg-emerald-700"
                            : "bg-[#2F5D8A] text-white hover:bg-[#274d72]"
                      }`}
                      data-testid={`client-photo-btn-${type.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {busy ? (
                        <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…</>
                      ) : done ? (
                        <><CheckCircle2 className="h-3.5 w-3.5" /> Upload photos</>
                      ) : (
                        <><Camera className="h-3.5 w-3.5" /> Upload photos</>
                      )}
                    </button>
                  </div>
                  <input
                    ref={el => { inputRefs.current[type] = el; }}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={ev => handleChange(type, ev)}
                  />
                </div>
              );
            })}
          </div>
        )}

        {errorMsg && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-[12px] text-red-700">
            {errorMsg}
          </div>
        )}

        <p className="text-[11px] text-gray-400 text-center mt-6">
          You can close this page anytime — uploaded photos are saved automatically.
        </p>
      </div>
    </div>
  );
}
